/**
 * AI Marketer Assistant client (T14, Wave 3 / C6).
 *
 * Phase-1 Copilot additions:
 *   - `Citation` (show-your-work) + `AssistantTurn.citations?`.
 *   - `askStream()` — SSE streaming of the same tool-loop answer via fetch +
 *     ReadableStream, with graceful degradation back to `ask()` (kept).
 */
import { apiClient, getTenantOverride, ApiError } from "./client";

export interface AssistantToolCall {
  tool: string;
  input: Record<string, unknown>;
  output?: unknown;
}

/** Show-your-work source chip emitted by the engine (collectCitations). */
export interface Citation {
  tool: string;
  label: string;
  count?: number | null;
}

/**
 * 1c — clickable action-chip emitted by the engine on the assistant turn.
 *
 * The chip says "this answer can be turned into an action" (e.g. «Создать этот
 * сегмент»). The payload lives in the matching tool_call output, NOT in the
 * chip — the page resolves `turn.tool_calls.find(tc => tc.tool === chip.tool)`,
 * runs the matching card-model reader and calls the existing apply-handler
 * (which routes through the preview/approve modal — NO SILENT WRITE).
 */
export interface ActionChip {
  /** Which handler to invoke (create_segment | broadcast_on_segment | draft_broadcast | create_journey | publish_survey | create_experience). */
  kind: string;
  /** Button label, e.g. «Создать этот сегмент». */
  label: string;
  /** Name of the tool_call whose output carries the payload (e.g. draft_segment). */
  tool: string;
  /** Index of the source tool_call in turn.tool_calls[] — UI resolves the payload by this (absent on older persisted turns). */
  toolIndex?: number;
}

export interface AssistantTurn {
  role: "user" | "assistant";
  content: string;
  tool_calls?: AssistantToolCall[];
  /** 1d — sources the assistant used, rendered as a «Источники:» chip row. */
  citations?: Citation[];
  /** 1c — action-chips (create-this-segment …) rendered under the message. */
  actions?: ActionChip[];
  /** §H — cost of this turn in kopecks (inline "Этот запрос: ₽X" badge). */
  ai_cost?: number;
}

export interface AskAssistantInput {
  prompt: string;
  history?: AssistantTurn[];
}

/**
 * D4 — AI spend vs daily budget for the active tenant (kopecks).
 *   spent_kopecks  — what the tenant has spent on AI today.
 *   budget_kopecks — daily AI budget; null = no limit.
 *   provider       — active LLM provider id (anthropic/yandex/gigachat/mock).
 */
export interface AiCostSummary {
  spent_kopecks: number;
  budget_kopecks: number | null;
  provider: string;
}

// ─── SSE streaming (1b) ───

/** Engine SSE event payloads (SPEC §3). */
export interface ToolCallEvent {
  tool: string;
  input: Record<string, unknown>;
  /** Gerund progress label, e.g. «Ищу опросы…». */
  label: string;
}
export interface ToolResultEvent {
  tool: string;
  ok: boolean;
  /** Short human summary, e.g. «Опросы: 3». */
  summary: string;
}
export interface TextDeltaEvent {
  delta: string;
}
export interface DoneEvent {
  turn: AssistantTurn;
}
export interface StreamErrorEvent {
  error: string;
  message?: string;
}
/** §H — final usage/cost of the turn (kopecks), emitted just before `done`. */
export interface UsageEvent {
  ai_cost: number;
  input_tokens: number;
  output_tokens: number;
  provider: string;
  model: string;
}

export interface AskStreamHandlers {
  onToolCall(d: ToolCallEvent): void;
  onToolResult(d: ToolResultEvent): void;
  onTextDelta(d: TextDeltaEvent): void;
  onDone(d: DoneEvent): void;
  onError(e: StreamErrorEvent): void;
  /** §H — optional: cost of this turn (kopecks). Absent on stub/zero-token turns. */
  onUsage?(d: UsageEvent): void;
}

/** Base URL mirrors api/client (nginx '/api' in prod, vite proxy in dev). */
const API_BASE = (import.meta.env.VITE_API_BASE ?? "/api") as string;

/**
 * Parse a buffer of one-or-more SSE frames separated by a blank line.
 * Returns the parsed events plus the trailing (incomplete) remainder so the
 * caller can prepend it to the next chunk. Each frame's `data:` lines are
 * concatenated (per the SSE spec) and JSON-parsed.
 */
export function parseSseFrames(
  buffer: string,
): { events: { event: string; data: unknown }[]; rest: string } {
  // Normalise CRLF → LF so the \n\n split is robust to proxies.
  const norm = buffer.replace(/\r\n/g, "\n");
  const parts = norm.split("\n\n");
  // The last element is a possibly-incomplete frame — keep it for next time.
  const rest = parts.pop() ?? "";
  const events: { event: string; data: unknown }[] = [];
  for (const frame of parts) {
    if (!frame.trim()) continue;
    let event = "message";
    const dataLines: string[] = [];
    for (const lineRaw of frame.split("\n")) {
      const line = lineRaw.replace(/^﻿/, "");
      if (line.startsWith(":")) continue; // comment / heartbeat
      if (line.startsWith("event:")) {
        event = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        // A single leading space after the colon is part of the framing.
        dataLines.push(line.slice(5).replace(/^ /, ""));
      }
    }
    const dataStr = dataLines.join("\n");
    let data: unknown = dataStr;
    if (dataStr.length) {
      try {
        data = JSON.parse(dataStr);
      } catch {
        data = dataStr; // non-JSON payload — pass through verbatim
      }
    }
    events.push({ event, data });
  }
  return { events, rest };
}

/** Dispatch one parsed SSE event to the matching handler. */
function dispatchSse(event: string, data: unknown, h: AskStreamHandlers): void {
  switch (event) {
    case "tool_call":
      h.onToolCall(data as ToolCallEvent);
      break;
    case "tool_result":
      h.onToolResult(data as ToolResultEvent);
      break;
    case "text":
      h.onTextDelta(data as TextDeltaEvent);
      break;
    case "done":
      h.onDone(data as DoneEvent);
      break;
    case "usage":
      h.onUsage?.(data as UsageEvent);
      break;
    case "error":
      h.onError(data as StreamErrorEvent);
      break;
    default:
      // Unknown event type (forward-compat) — ignore.
      break;
  }
}

export const aiAssistantApi = {
  async ask(input: AskAssistantInput): Promise<{ turn: AssistantTurn; ai_cost?: number }> {
    // FIX-AI-6: the assistant runs a multi-step tool loop on the backend
    // (assistant → create_journey → NLToJourney → assistant …); the default
    // ~30s client timeout aborted long chains. Allow up to 120s.
    // §H — response now also carries `ai_cost` (kopecks) for the inline badge.
    const { data } = await apiClient.post("/v1/ai-assistant/ask", input, { timeout: 120_000 });
    return data;
  },

  /**
   * Stream the answer via SSE (1b). Pushes live tool-call/result/text events
   * to `handlers`; resolves when the stream ends. On a network/stream failure
   * it calls `handlers.onError` (the page falls back to `ask()`).
   *
   * The budget pre-gate returns a regular JSON 409 (AI_BUDGET_EXCEEDED) BEFORE
   * the stream — surfaced as a thrown ApiError so the page reuses its existing
   * 409 handling.
   */
  async askStream(
    input: AskAssistantInput,
    handlers: AskStreamHandlers,
    signal?: AbortSignal,
  ): Promise<void> {
    const override = getTenantOverride();
    const res = await fetch(`${API_BASE}/v1/ai-assistant/ask/stream`, {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        ...(override ? { "X-Tenant-Override": override } : {}),
      },
      body: JSON.stringify(input),
      signal,
    });

    // Budget pre-gate (and any other non-stream failure) comes back as JSON,
    // not text/event-stream. Re-throw as ApiError so the page's 409 branch fires.
    const contentType = res.headers.get("content-type") ?? "";
    if (!res.ok || !contentType.includes("text/event-stream")) {
      let payload: Record<string, unknown> | undefined;
      let bodyText = "";
      try {
        bodyText = await res.text();
        payload = bodyText ? (JSON.parse(bodyText) as Record<string, unknown>) : undefined;
      } catch {
        /* non-JSON body — leave payload undefined */
      }
      const code = typeof payload?.error === "string" ? payload.error : undefined;
      const message =
        (typeof payload?.message === "string" && payload.message) ||
        code ||
        `HTTP ${res.status}`;
      throw new ApiError(message, res.status, code, undefined, payload);
    }

    if (!res.body) {
      // No streaming body (older browsers / proxies) — let the page fall back.
      handlers.onError({ error: "NO_STREAM", message: "Streaming not supported" });
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { events, rest } = parseSseFrames(buffer);
        buffer = rest;
        for (const ev of events) dispatchSse(ev.event, ev.data, handlers);
      }
      // Flush any trailing complete frame left after the final chunk.
      buffer += decoder.decode();
      if (buffer.trim()) {
        const { events } = parseSseFrames(buffer + "\n\n");
        for (const ev of events) dispatchSse(ev.event, ev.data, handlers);
      }
    } catch (e: unknown) {
      handlers.onError({
        error: "STREAM_ABORTED",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  },

  async costSummary(): Promise<AiCostSummary> {
    const { data } = await apiClient.get("/v1/ai/cost-summary");
    return data;
  },

  /** §H plan B — the CURRENT user's own AI spend (today + last 30d). Any role. */
  async myUsage(): Promise<MyAiUsage> {
    const { data } = await apiClient.get("/v1/me/ai-usage");
    return data;
  },
};

/** §H plan B — GET /v1/me/ai-usage payload (this user's own AI spend). */
export interface MyAiUsage {
  today: { spend_kopecks: number; calls: number };
  last_30d: {
    spend_kopecks: number;
    calls: number;
    by_feature: Array<{ feature: string; spend_kopecks: number; calls: number }>;
  };
}
